"use client"

import { useState, useEffect, useCallback, useId } from "react"
import { ShieldCheck, Fingerprint, KeyRound, X, Loader2 } from "lucide-react"
import Link from "next/link"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { startAuthentication } from "@simplewebauthn/browser"
import type { DestructiveGuardMode } from "@/lib/types/ai-chat"
import { fetchApi } from "@/lib/api/client"
import { StepUpFactorPicker, type StepUpFactor } from "./step-up-factor-picker"
import { useStepUpVerify, useStepUpInitiate } from "@/components/settings/hooks/use-ai-chat"
import { useChatContext } from "@/lib/ai-chat/chat-context"
import { useQuery } from "@tanstack/react-query"
import { resolveToolLabel, shortenId, summarizeApprovedInput, tagApprovalSubject, vishingApprovalKey, vishingCampaignSubject, type SelectionFilter, type TagApprovalSubject, type VishingCampaignSubject } from "@/lib/ai-chat/tool-display"
import { useTranslation } from '@/lib/i18n'
import { foldDigits } from '@/lib/text/fold-digits'
import { UserText } from '@/components/ui/user-text'

// ── Guard-mode resolution (WS9 deploy tolerance) ───────────────────────────────
//
// The dialog must drive its confirm-tier UI off the SERVER-computed
// `effectiveGuardMode`, never off the raw `destructiveGuardMode` — that
// distinction is the whole point of this type (see lib/types/ai-chat.ts). A
// backend-only deploy, or a stale open tab running an older bundle, can hand
// this component a value it does not recognise (a future mode, or a typo in
// transit). Falling through to "nothing renders" is the exact failure this
// fixes: it must degrade to the plain-confirm path, never an empty dialog.
const KNOWN_GUARD_MODES: readonly DestructiveGuardMode[] = [
  "confirm",
  "confirmation_code",
  "passkey",
]

function resolveGuardMode(raw: unknown): DestructiveGuardMode {
  return typeof raw === "string" &&
    (KNOWN_GUARD_MODES as readonly string[]).includes(raw)
    ? (raw as DestructiveGuardMode)
    : "confirm"
}

// ── Approved-input summary ──────────────────────────────────────────────────
//
// The server-issued approval token binds to a hash of the tool's INPUT, not
// just its name (see StepUpService.checkBinding). Printing only the tool name
// asked the human to approve arguments they never saw — and the first version
// of this fix over-corrected by printing the raw argument KEYS in monospace
// with JSON.stringify for anything non-scalar, which is exactly the kind of
// internal detail (an "ids" key, a bracketed array, a workspaceId uuid) a
// non-technical approver cannot act on. summarizeApprovedInput
// (lib/ai-chat/tool-display.ts) does the real work: it drops tenant-plumbing
// keys, drops an opaque id ONLY when something else already names the
// subject, and renders every value as words — never JSON.
//
// WHICH CAMPAIGN, AND HOW MANY PEOPLE. A Vishing campaign action carries only
// the campaign's uuid. For those tools the dialog reads the campaign and shows
// its name, plus the number of people a launch would call or a resume still has
// to. A launch re-resolves its audience, so the number is the audience check's
// fresh figure, with the count stored on the campaign (eligibleNow) as the
// fallback. When the campaign cannot be read the reference is kept and the
// dialog says so: an approval is never left with nothing naming its subject.
const asNumber = (value: unknown): number | null => (typeof value === 'number' && Number.isFinite(value) ? value : null)
const asRecord = (value: unknown): Record<string, unknown> => (value && typeof value === 'object' ? { ...value } : {})
function useVishingCampaignApproval(subject: VishingCampaignSubject | null) {
  return useQuery({
    queryKey: ['vishing', 'campaigns', 'approval', subject ? subject.id : '', subject ? subject.domainId : null, subject ? subject.people : null],
    enabled: !!subject,
    staleTime: 0,
    retry: false,
    queryFn: async ({ signal }): Promise<{ name: string; people: number | null }> => {
      if (!subject) throw new Error('no campaign')
      const headers: Record<string, string> = subject.domainId ? { 'x-domain-id': subject.domainId } : {}
      const campaign = asRecord(await fetchApi<unknown>(`/vishing/campaigns/${subject.id}`, { signal, headers }))
      if (typeof campaign.name !== 'string' || !campaign.name) throw new Error('campaign unavailable')
      if (subject.people === 'resume') return { name: campaign.name, people: asNumber(campaign.remainingTargets) }
      if (subject.people !== 'launch') return { name: campaign.name, people: null }
      const preview = asRecord(await fetchApi<unknown>(`/vishing/campaigns/${subject.id}/audience-preview`, { method: 'POST', signal, headers }).catch(() => null))
      const fresh = asNumber(asRecord(preview.summary).eligible)
      return { name: campaign.name, people: fresh !== null ? fresh : asNumber(campaign.eligibleNow) }
    },
  })
}
// WHICH TAG, AND WHO (#932). A tag action carries only ids, so the box showed
// "Recipient IDs: 1 recipient ids". For those tools the dialog reads the tags
// and the people and names them: the tag names, and the emails of the first
// TAG_APPROVAL_PEOPLE people with how many more, and of the first people a
// select-all leaves out. What cannot be read keeps its reference and the
// dialog says so, as for a campaign; the tags and the people are read apart,
// so one person in the Recycle Bin does not hide the tag. What the call itself
// says (the action, everyone or the filters) shows at once, before any name.
const TAG_APPROVAL_PEOPLE = 5
/** null: that half could not be read. A person not in the list is null. */
interface TagApprovalNames {
  tags: string[] | null
  people: (string | null)[] | null
  excluded: (string | null)[] | null
}
function useTagApproval(subject: TagApprovalSubject | null) {
  const shownPeople = subject ? subject.recipientIds.slice(0, TAG_APPROVAL_PEOPLE) : []
  const shownExcluded = subject ? subject.excludedIds.slice(0, TAG_APPROVAL_PEOPLE) : []
  return useQuery({
    queryKey: ['recipients', 'tags', 'approval', subject ? subject.tagIds : [], shownPeople, shownExcluded],
    enabled: !!subject,
    staleTime: 0,
    retry: false,
    queryFn: async ({ signal }): Promise<TagApprovalNames> => {
      if (!subject) throw new Error('no tag action')
      const unread = (error: unknown) => {
        if (signal.aborted) throw error
        return null
      }
      const readTags = Promise.all(
        subject.tagIds.map(async (tagId) => {
          const tag = asRecord(await fetchApi<unknown>(`/recipients/tags/${tagId}`, { signal }))
          if (typeof tag.name !== 'string' || !tag.name) throw new Error('tag unavailable')
          return tag.name
        }),
      ).catch(unread)
      const readEmails = async (): Promise<Map<string, string>> => {
        const wanted = [...shownPeople, ...shownExcluded]
        const emails = new Map<string, string>()
        if (wanted.length === 0) return emails
        const query = new URLSearchParams()
        for (const personId of wanted) query.append('ids', personId)
        query.set('limit', String(wanted.length))
        const page = asRecord(await fetchApi<unknown>(`/recipients?${query.toString()}`, { signal }))
        for (const row of Array.isArray(page.data) ? page.data.map(asRecord) : []) {
          // PostgreSQL answers with lower-case ids; the call may have sent capitals
          if (typeof row.id === 'string' && typeof row.email === 'string' && row.email) emails.set(row.id.toLowerCase(), row.email)
        }
        return emails
      }
      const [tags, emails] = await Promise.all([readTags, readEmails().catch(unread)])
      const emailsOf = (ids: string[]) => (emails ? ids.map((personId) => emails.get(personId.toLowerCase()) ?? null) : null)
      return { tags, people: emailsOf(shownPeople), excluded: emailsOf(shownExcluded) }
    },
  })
}

function ApprovedInputSummary({ input, toolName = '', vishing = false }: { input: Record<string, unknown>; toolName?: string; vishing?: boolean }) {
  const { t } = useTranslation()
  const ignoredHeadingId = useId()
  const subject = vishingCampaignSubject(toolName, input)
  const campaign = useVishingCampaignApproval(subject)
  const tagSubject = tagApprovalSubject(toolName, input)
  const tagNames = useTagApproval(tagSubject)
  // Once the campaign, or the tags and the people, are shown by name their
  // ids add nothing; until then the generic rule decides.
  const shownKeys = subject ? ['id'] : tagSubject ? tagSubject.shownKeys : []
  const { args, allHidden } = summarizeApprovedInput(shownKeys.length > 0 ? Object.fromEntries(Object.entries(input).filter(([key]) => !shownKeys.includes(key))) : input)

  if (tagSubject) {
    const names = tagNames.data
    const separator = t('aiChat.approval.separator')
    const people = tagSubject.recipientIds.length
    const unreadable =
      tagNames.isError ||
      (!!names && (names.tags === null || names.people === null || names.excluded === null || names.people.includes(null) || names.excluded.includes(null)))
    // count 0 is a filter selection: no count is known before the call runs
    const peopleLabel = t('aiChat.approval.people', { count: tagSubject.allMatching ? 0 : people })
    // the first TAG_APPROVAL_PEOPLE by email once read (until then, or when
    // one cannot be read, by short id), then how many more
    const personList = (ids: string[], emails: (string | null)[] | null | undefined) => (
      <>
        {ids.slice(0, TAG_APPROVAL_PEOPLE).map((personId, index) => (
          <span key={personId}>
            {index > 0 && separator}
            <UserText dir="ltr">{emails?.[index] ?? shortenId(personId)}</UserText>
          </span>
        ))}
        {ids.length > TAG_APPROVAL_PEOPLE && <span> {t('aiChat.approval.more', { count: ids.length - TAG_APPROVAL_PEOPLE })}</span>}
      </>
    )
    // one span per value, so a list of Arabic and Latin values keeps its order
    const filterRow = ({ key, label, values }: SelectionFilter) => (
      <div key={`filter-${key}`} className="flex gap-2 ps-3">
        <dt className="text-muted-foreground shrink-0">{label}:</dt>
        <dd className="break-all">
          {values.map((value, index) => (
            <span key={index}>
              {index > 0 && separator}
              <UserText>{value}</UserText>
            </span>
          ))}
        </dd>
      </div>
    )
    // The group is the widest the call can reach: only filters the API is
    // sure to apply narrow it; what it may ignore is listed below, apart.
    const everyone = tagSubject.filters.length > 0
      ? t('aiChat.approval.everyoneMatching')
      : tagSubject.mayBeIgnored.length > 0
        ? t('aiChat.approval.everyone')
        : t('aiChat.approval.everyoneInWorkspace')
    return (
      <div
        data-testid="approved-input-summary"
        className="px-3 py-2 rounded-lg bg-muted/50 border border-border text-xs space-y-1.5"
      >
        <p className="font-medium text-muted-foreground">{t('aiChat.approval.approving')}</p>
        <dl className="space-y-0.5">
          <div className="flex gap-2">
            <dt className="text-muted-foreground shrink-0">{t('aiChat.approval.action')}:</dt>
            <dd className="font-medium">
              {tagSubject.action === 'add'
                ? t('aiChat.approval.add')
                : tagSubject.action === 'remove'
                  ? t('aiChat.approval.remove')
                  : t('aiChat.approval.replace')}
            </dd>
          </div>
        </dl>
        {tagNames.isPending && <p role="status" className="text-muted-foreground">{t('aiChat.approval.reading')}</p>}
        {unreadable && <p role="alert" className="text-destructive">{t('aiChat.approval.unavailable')}</p>}
        <dl className="space-y-0.5">
          <div className="flex gap-2">
            <dt className="text-muted-foreground shrink-0">{t('aiChat.approval.tags', { count: tagSubject.tagIds.length })}:</dt>
            <dd className="break-all">
              {tagSubject.tagIds.length === 0
                ? t('aiChat.approval.noTags')
                : tagSubject.tagIds.map((tagId, index) => (
                    // one span per name, so a list of Arabic and Latin names keeps its order
                    <span key={tagId}>
                      {index > 0 && separator}
                      {names?.tags ? (
                        <UserText className="font-medium">{names.tags[index]}</UserText>
                      ) : (
                        <UserText dir="ltr">{shortenId(tagId)}</UserText>
                      )}
                    </span>
                  ))}
            </dd>
          </div>
          <div className="flex gap-2">
            <dt className="text-muted-foreground shrink-0">{peopleLabel}:</dt>
            <dd className="break-all">{tagSubject.allMatching ? everyone : personList(tagSubject.recipientIds, names?.people)}</dd>
          </div>
          {tagSubject.filters.map(filterRow)}
          {tagSubject.excludedIds.length > 0 && (
            <div className="flex gap-2">
              <dt className="text-muted-foreground shrink-0">{t('aiChat.approval.except')}:</dt>
              <dd className="break-all">{personList(tagSubject.excludedIds, names?.excluded)}</dd>
            </div>
          )}
          {tagSubject.allMatching && people > 0 && (
            <div className="flex gap-2">
              <dt className="text-muted-foreground shrink-0">{t('aiChat.approval.also')}:</dt>
              <dd className="break-all">{personList(tagSubject.recipientIds, names?.people)}</dd>
            </div>
          )}
        </dl>
        {tagSubject.mayBeIgnored.length > 0 && (
          <div className="space-y-0.5 border-t border-dashed border-border pt-1.5">
            <p id={ignoredHeadingId} className="text-muted-foreground">{t('aiChat.approval.mayBeIgnored')}:</p>
            <dl aria-labelledby={ignoredHeadingId} className="space-y-0.5">{tagSubject.mayBeIgnored.map(filterRow)}</dl>
          </div>
        )}
        {args.length > 0 && (
          <dl className="space-y-0.5">
            {args.map(({ key, label, value }) => (
              <div key={key} className="flex gap-2">
                <dt className="text-muted-foreground shrink-0">{label}:</dt>
                <dd className="break-all"><UserText dir={/id$/i.test(key) ? 'ltr' : 'auto'}>{value}</UserText></dd>
              </div>
            ))}
          </dl>
        )}
      </div>
    )
  }

  if (subject) {
    return (
      <div
        data-testid="approved-input-summary"
        className="px-3 py-2 rounded-lg bg-muted/50 border border-border text-xs space-y-1.5"
      >
        <p className="font-medium text-muted-foreground">{t('vishing.ai.approving')}</p>
        {campaign.isPending && <p role="status" className="text-muted-foreground">{t('vishing.ai.subject.reading')}</p>}
        {campaign.isError && (
          <>
            <p role="alert" className="text-destructive">{t('vishing.ai.subject.unavailable')}</p>
            <div className="flex gap-2">
              <span className="text-muted-foreground shrink-0">{t('vishing.ai.arguments.id')}:</span>
              <UserText dir="ltr">{shortenId(subject.id)}</UserText>
            </div>
          </>
        )}
        {campaign.data && (
          <>
            <div className="flex gap-2">
              <span className="text-muted-foreground shrink-0">{t('vishing.ai.subject.campaign')}:</span>
              <UserText className="font-medium">{campaign.data.name}</UserText>
            </div>
            {campaign.data.people !== null && (
              <p className="font-medium">
                {t(subject.people === 'resume' ? 'vishing.campaigns.confirm.remaining' : 'vishing.campaigns.confirm.people', { count: campaign.data.people })}
              </p>
            )}
          </>
        )}
        {args.length > 0 && (
          <dl className="space-y-0.5">
            {args.map(({ key, label, value }) => (
              <div key={key} className="flex gap-2">
                <dt className="text-muted-foreground shrink-0">{['callerId','transportId','scheduledAt','reason'].includes(key) ? t('vishing.ai.arguments.'+key) : label}:</dt>
                <dd className="break-all"><UserText dir={/id$|callerId|scheduledAt/i.test(key) ? 'ltr' : 'auto'}>{value}</UserText></dd>
              </div>
            ))}
          </dl>
        )}
      </div>
    )
  }

  if (args.length === 0) {
    if (!allHidden) return null
    // Everything in the input was tenant plumbing (workspace/company/domain
    // scoping) — there is no per-call detail to show, but the box must not
    // just vanish, or the approver loses the one confirmation that this
    // dialog is about a specific call at all.
    return (
      <div
        data-testid="approved-input-summary"
        className="px-3 py-2 rounded-lg bg-muted/50 border border-border text-xs text-muted-foreground"
      >
        {vishing ? t('vishing.ai.currentWorkspace') : 'No extra details to review — this applies to your current workspace.'}
      </div>
    )
  }

  return (
    <div
      data-testid="approved-input-summary"
      className="px-3 py-2 rounded-lg bg-muted/50 border border-border text-xs space-y-1.5"
    >
      <p className="font-medium text-muted-foreground">{vishing ? t('vishing.ai.approving') : 'You’re approving:'}</p>
      <dl className="space-y-0.5">
        {args.map(({ key, label, value }) => (
          <div key={key} className="flex gap-2">
            <dt className="text-muted-foreground shrink-0">{vishing && ['id','callerId','transportId','scheduledAt','reason'].includes(key) ? t('vishing.ai.arguments.'+key) : label}:</dt>
            <dd className="break-all"><UserText dir={/id$|callerId|scheduledAt/i.test(key) ? 'ltr' : 'auto'}>{value}</UserText></dd>
          </div>
        ))}
      </dl>
    </div>
  )
}

// ── Props ────────────────────────────────────────────────────────────────────

type BaseProps = {
  open: boolean
  onOpenChange: (v: boolean) => void
  toolName: string
  /** Server-authored human label for `toolName`, when the backend sent one. */
  toolLabel?: string
  input: Record<string, unknown>
  conversationId: string
  onConfirm: () => Promise<void> | void
  onCancel: () => void
}

export type VerificationDialogProps = BaseProps &
  (
    | { requirement: "confirm"; message?: string }
    | {
        requirement: "step-up"
        factors: StepUpFactor[]
        verifyReason: string
        passkeyOptions?: unknown
        /**
         * Server-authored explanation, carried on the `verification_required`
         * frame. It was previously confirm-only, so the step-up branch had no
         * way to render the one case that most needs words: `factors: []`,
         * where the account cannot satisfy the gate at all and the message is
         * the only thing telling the user what to do about it.
         */
        message?: string
      }
  )

// ── Component ────────────────────────────────────────────────────────────────

export function VerificationDialog(props: VerificationDialogProps) {
  const { open, onOpenChange, toolName, toolLabel, onConfirm, onCancel } = props
  const { t } = useTranslation()
  const isVishing = toolName.startsWith('vishing_')
  const copy = (key: string, original: string) => isVishing ? t(key) : original
  const approvalKey = vishingApprovalKey(toolName)

  // Step-up path hooks
  const stepUpVerify = useStepUpVerify()
  // Fetch server-issued WebAuthn options before opening the authenticator.
  // Root cause: passkeyOptions was never fetched — startAuthentication received undefined.
  // Call useStepUpInitiate on mount whenever step-up + passkey are required,
  // then forward the returned passkeyOptions to the picker. The assertion sent
  // back does not echo a client-controlled expected challenge.
  const stepUpInitiate = useStepUpInitiate()
  const { setStepUpToken } = useChatContext()

  // ── Step-up passkey options state ────────────────────────────────────────
  const [fetchedPasskeyOptions, setFetchedPasskeyOptions] = useState<Record<string, unknown> | undefined>(undefined)

  // ── Confirm-tier state ────────────────────────────────────────────────────
  // Default to 'confirm' — the safe fallback (see resolveGuardMode above) —
  // rather than 'confirmation_code', which used to demand a code that,
  // platform-wide, does not exist for any workspace until an admin sets one.
  const [mode, setMode] = useState<DestructiveGuardMode>("confirm")
  const [hasPasskeys, setHasPasskeys] = useState(false)
  const [code, setCode] = useState("")
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  const [settingsLoaded, setSettingsLoaded] = useState(false)

  // Fetch passkey challenge on mount when step-up + passkey factor is required.
  // Without this, passkeyOptions is always undefined and startAuthentication fails.
  useEffect(() => {
    if (!open || props.requirement !== "step-up") return
    if (!props.factors.includes("passkey")) return
    setFetchedPasskeyOptions(undefined)
    stepUpInitiate.mutate(
      { toolName, input: props.input, conversationId: props.conversationId },
      {
        onSuccess: (data) => {
          if (data.passkeyOptions) {
            setFetchedPasskeyOptions(data.passkeyOptions as Record<string, unknown>)
          }
        },
      },
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, props.requirement])

  // Fetch settings only when showing the confirm-tier variant.
  //
  // Drives off `effectiveGuardMode` — the server-computed value that already
  // accounts for an unconfigured code (falls back to 'confirm') or a
  // 'passkey' mode with no enrolled passkey (fails closed) — never off the
  // raw `destructiveGuardMode`. Any value this bundle doesn't recognise (a
  // future mode, a BE-only deploy landing on a stale tab) degrades to
  // 'confirm' rather than rendering a dialog with no way to act.
  useEffect(() => {
    if (!open || props.requirement !== "confirm") return
    setCode("")
    setError("")
    setLoading(false)
    const settingsUrl = "/settings/ai-chat"
    fetchApi<{ effectiveGuardMode?: unknown; passkeys?: unknown[] }>(settingsUrl)
      .then((s) => {
        setMode(resolveGuardMode(s.effectiveGuardMode))
        setHasPasskeys((s.passkeys || []).length > 0)
        setSettingsLoaded(true)
      })
      .catch(() => {
        // A failed settings fetch must still let the user through via plain
        // confirm — the alternative is stranding them on an empty dialog.
        setMode("confirm")
        setSettingsLoaded(true)
      })
  }, [open, props.requirement])

  // Obtain the server-issued approval for the Confirm tier and hand it to the
  // executor out-of-band.
  //
  // The old gate was `confirmed: true` inside the tool ARGUMENTS — a field the
  // model writes. The model could therefore approve its own destructive action,
  // and a prompt injection could start a campaign with no human involved. The
  // token below is minted server-side and bound to this exact tool + input, so
  // it authorises one action and nothing else.
  //
  // ONE-SHOT (B1 fix): `POST /ai-chat/step-up/initiate` now resolves the
  // workspace's EFFECTIVE guard mode and, when it demands a credential,
  // verifies it IN THIS SAME REQUEST before minting a token
  // (ai-chat-settings.controller.ts `initiateStepUp`). There is no longer a
  // separate `/settings/ai-chat/verify` call to make first — this function is
  // the ONLY place a Confirm-tier approval is requested, for all three modes:
  // 'confirm' (no `code`/`credential` needed), 'confirmation_code' (`code`),
  // and 'passkey' (`credential`). Calling initiate with no credential in a
  // workspace that requires one used to still mint a valid token — the
  // caller could just skip the verify step.
  const approveConfirmTier = useCallback(
    async (extra?: { code?: string; credential?: Record<string, unknown> }) => {
      const data = await stepUpInitiate.mutateAsync({
        toolName,
        input: props.input,
        conversationId: props.conversationId,
        ...(extra?.code !== undefined ? { code: extra.code } : {}),
        ...(extra?.credential !== undefined ? { credential: extra.credential } : {}),
      })
      if (data.confirmationToken && data.confirmationExpiresAt) {
        setStepUpToken(data.confirmationToken, data.confirmationExpiresAt)
        return
      }
      // The request reached the server (it did not throw), but the server
      // declined to mint — surface ITS reason rather than a made-up one. A
      // wrong code / rejected passkey lands here with `confirmationError` set
      // to the server's exact words (e.g. "No confirmation code is
      // configured for this workspace"); inventing a client-side string here
      // is exactly how a wrong code used to be told "Invalid confirmation
      // code" instead of the truth.
      throw new Error(data.confirmationError || "Approval could not be issued — please try again.")
    },
    [stepUpInitiate, toolName, props.input, props.conversationId, setStepUpToken],
  )

  // Plain-confirm path (effectiveGuardMode === 'confirm'): an authenticated
  // session is the whole requirement, so this mints the token directly — no
  // code or passkey prompt to dead-end on.
  const handlePlainConfirm = useCallback(async () => {
    setLoading(true)
    setError("")
    try {
      await approveConfirmTier()
      await onConfirm()
      // Deliberately NOT closing here. Every consumer wires
      // `onOpenChange={(v) => !v && handleVerificationCancel()}`, so a
      // self-close after SUCCESS was indistinguishable from the user
      // dismissing the dialog — it posted "I cancelled the verification"
      // straight after "Completed successfully", telling the user their
      // action had failed when it had not. The parent closes the dialog from
      // its own state once onConfirm resolves.
    } catch (e) {
      setError(e instanceof Error && e.message ? e.message : "Could not confirm. Please try again.")
    } finally {
      setLoading(false)
    }
  }, [approveConfirmTier, onConfirm, onOpenChange])

  // ONE-SHOT (B1 fix): used to POST /settings/ai-chat/verify first, then call
  // approveConfirmTier() with NO code — two independent round-trips a direct
  // caller could skip, and the second call minted a token unconditionally
  // regardless of whether the first one ever happened. The code now rides on
  // the SAME initiate request that verifies it and mints the token
  // (approveConfirmTier), so there is nothing left to call separately.
  const handleCodeVerify = useCallback(async () => {
    if (!code.trim()) {
      setError("Please enter the confirmation code")
      return
    }
    setLoading(true)
    setError("")
    try {
      await approveConfirmTier({ code: isVishing ? foldDigits(code.trim()) : code.trim() })
      await onConfirm()
      // Deliberately NOT closing here. Every consumer wires
      // `onOpenChange={(v) => !v && handleVerificationCancel()}`, so a
      // self-close after SUCCESS was indistinguishable from the user
      // dismissing the dialog — it posted "I cancelled the verification"
      // straight after "Completed successfully", telling the user their
      // action had failed when it had not. The parent closes the dialog from
      // its own state once onConfirm resolves.
    } catch (e) {
      // approveConfirmTier throws with the SERVER's own reason (e.g. "No
      // confirmation code is configured for this workspace") when the code is
      // wrong or missing — never invent "Invalid confirmation code" here,
      // that told a user with no code configured that their code was wrong.
      setError(e instanceof Error && e.message ? e.message : "Verification failed. Please try again.")
    } finally {
      setLoading(false)
    }
  }, [code, approveConfirmTier, onConfirm, onOpenChange, isVishing])

  // ONE-SHOT (B1 fix): the WebAuthn challenge still has to come from a
  // dedicated call — a browser cannot produce an assertion without a
  // server-issued challenge first, and `POST /ai-chat/step-up/initiate` does
  // NOT generate one for this path (its own `passkeyOptions` field is for a
  // different guard entirely — the Verify-tier step-up FACTOR policy, gated
  // on `policy.factors.includes('passkey')`, not this Confirm-tier
  // `effectiveGuardMode==='passkey'` path). So the sequence stays two calls:
  //   1. POST /settings/ai-chat/passkey/auth-options — get the challenge
  //      (purpose: 'ai-identity'; unchanged by this fix, and not a
  //      verification step, so there is nothing to fold it into).
  //   2. approveConfirmTier({ credential }) → POST /ai-chat/step-up/initiate
  //      — verifies the assertion AND mints the token in ONE request.
  // What's gone is the THIRD call this used to make: a separate
  // POST /settings/ai-chat/verify between steps 1 and 2, whose result a
  // direct caller could ignore before hitting initiate with no credential at
  // all.
  const handlePasskeyVerify = useCallback(async () => {
    setLoading(true)
    setError("")
    try {
      // Step 1: Get the WebAuthn challenge from the server.
      const authOptsUrl = "/settings/ai-chat/passkey/auth-options"
      const options = await fetchApi<Record<string, unknown>>(authOptsUrl, {
        method: "POST",
      })

      // Step 2: Prompt the user for biometric (Touch ID / Face ID)
      const credential = await startAuthentication({ optionsJSON: options as unknown as import('@simplewebauthn/browser').StartAuthenticationOpts['optionsJSON'] })

      // Step 3: verify the assertion AND mint the approval token in the SAME
      // request (approveConfirmTier → initiate).
      await approveConfirmTier({ credential: credential as unknown as Record<string, unknown> })
      await onConfirm()
      // Deliberately NOT closing here. Every consumer wires
      // `onOpenChange={(v) => !v && handleVerificationCancel()}`, so a
      // self-close after SUCCESS was indistinguishable from the user
      // dismissing the dialog — it posted "I cancelled the verification"
      // straight after "Completed successfully", telling the user their
      // action had failed when it had not. The parent closes the dialog from
      // its own state once onConfirm resolves.
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Passkey verification failed"
      if (msg.includes("Cancelled") || msg.includes("AbortError") || msg.includes("NotAllowedError")) {
        setError("Verification cancelled.")
      } else {
        setError("Passkey verification failed. Try using confirmation code instead.")
        setMode("confirmation_code")
      }
    } finally {
      setLoading(false)
    }
  }, [approveConfirmTier, onConfirm, onOpenChange])



  // ── Step-up submit handler ────────────────────────────────────────────────

  const handleStepUpSubmit = useCallback(
    async (factor: StepUpFactor, payload: Record<string, unknown>) => {
      // `input` is load-bearing: the server binds the issued token to this
      // exact tool + arguments. Without it the token is UNBOUND and the
      // executor refuses it — one approval must not authorise every other
      // Verify-tier tool for the next five minutes.
      const result = await stepUpVerify.mutateAsync({
        factor,
        payload,
        toolName,
        input: props.input,
      })
      setStepUpToken(result.stepUpToken, result.expiresAt)
      await onConfirm()
      // Deliberately NOT closing here. Every consumer wires
      // `onOpenChange={(v) => !v && handleVerificationCancel()}`, so a
      // self-close after SUCCESS was indistinguishable from the user
      // dismissing the dialog — it posted "I cancelled the verification"
      // straight after "Completed successfully", telling the user their
      // action had failed when it had not. The parent closes the dialog from
      // its own state once onConfirm resolves.
    },
    [stepUpVerify, toolName, props.input, setStepUpToken, onConfirm, onOpenChange],
  )

  if (!open) return null

  // Was `toolName.replace(/_/g, " ").replace(...)` — a raw identifier with
  // the underscores swapped for spaces, e.g. "Content studio session
  // create". resolveToolLabel prefers the server-authored label and only
  // falls back to humanizing the id when one hasn't been sent (see
  // lib/ai-chat/tool-display.ts).
  const toolDisplayName = resolveToolLabel(toolName, toolLabel, t)

  // ── Step-up branch ────────────────────────────────────────────────────────

  if (props.requirement === "step-up") {
    // While the initiate call is in-flight, show a spinner so the user
    // doesn't click "Verify with passkey" before passkeyOptions arrives.
    const needsPasskeyOptions = props.factors.includes("passkey")
    const passkeyOptionsReady = !needsPasskeyOptions || fetchedPasskeyOptions !== undefined
    const initiating = needsPasskeyOptions && stepUpInitiate.isPending

    return (
      // #932 — above every layer the chat draws (its floating window is
      // z-[51]/z-[52]): at z-50 the chat covered the ×, the box's only
      // Cancel, and only Confirm could be clicked. The confirm branch below
      // sits on the same layer.
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="bg-background border border-border rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-6 pt-6 pb-2">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center h-10 w-10 rounded-full bg-destructive/10">
                <ShieldCheck className="h-5 w-5 text-destructive" />
              </div>
              <div>
                <h3 className="text-base font-semibold">{copy('vishing.ai.identityTitle', 'Identity Verification')}</h3>
                <p className="text-xs text-muted-foreground">{copy('vishing.ai.required', 'Required for this operation')}</p>
              </div>
            </div>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onCancel}>
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Body */}
          <div className="px-6 py-4 space-y-4">
            <div className="px-3 py-2 rounded-lg bg-destructive/5 border border-destructive/20 text-sm">
              <span className="font-medium">{copy('vishing.ai.operation', 'Operation:')}</span>{" "}
              <span className="text-muted-foreground">{toolDisplayName}</span>
            </div>

            <ApprovedInputSummary input={props.input} toolName={toolName} vishing={isVishing} />

            {props.factors.length === 0 ? (
              // No factor this account can present. The server resolved WHY
              // and put it on `message`; show that instead of a picker.
              // StepUpFactorPicker with an empty list renders `repeat(0, 1fr)`
              // and no buttons — an empty box the user cannot act on or learn
              // from, which is the same dead end the server fix removed.
              <div className="px-3 py-2 rounded-lg bg-muted/50 border border-border text-sm text-muted-foreground">
                {(isVishing ? t('vishing.ai.cannotVerify') : props.message) ||
                  "Your account cannot complete this verification. Ask your administrator to check your sign-in methods."}
              </div>
            ) : initiating ? (
              <div className="flex items-center justify-center gap-2 py-4 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                {copy('vishing.ai.preparing', 'Preparing verification…')}
              </div>
            ) : (
              <StepUpFactorPicker
                factors={props.factors}
                verifyReason={isVishing ? t('vishing.ai.verifyReason') : props.verifyReason}
                localized={isVishing}
                // Use freshly fetched passkeyOptions; fall back to the prop only if initiate
                // was not needed (non-passkey factors).
                passkeyOptions={passkeyOptionsReady ? fetchedPasskeyOptions : undefined}
                onSubmit={handleStepUpSubmit}
                onCancel={onCancel}
              />
            )}
          </div>
        </div>
      </div>
    )
  }

  // ── Confirm branch (existing UX — preserved) ──────────────────────────────

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-background border border-border rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-2">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center h-10 w-10 rounded-full bg-destructive/10">
              <ShieldCheck className="h-5 w-5 text-destructive" />
            </div>
            <div>
              <h3 className="text-base font-semibold">{copy('vishing.ai.confirmTitle', 'Identity Verification')}</h3>
              <p className="text-xs text-muted-foreground">{copy('vishing.ai.required', 'Required for destructive operations')}</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onCancel}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 space-y-4">
          {props.message && (
            <div className="px-3 py-2 rounded-lg bg-muted/50 border border-border text-sm text-muted-foreground">
              {isVishing && approvalKey ? t(approvalKey) : props.message}
            </div>
          )}

          <div className="px-3 py-2 rounded-lg bg-destructive/5 border border-destructive/20 text-sm">
            <span className="font-medium">{copy('vishing.ai.operation', 'Operation:')}</span>{" "}
            <span className="text-muted-foreground">{toolDisplayName}</span>
          </div>

          <ApprovedInputSummary input={props.input} toolName={toolName} vishing={isVishing} />

          {settingsLoaded && mode === "confirm" && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                {/* The trailing space matters: without it the sentence read "clickConfirm". */}
                {!isVishing && 'This workspace has no confirmation code or passkey configured yet — click '}
                {copy('vishing.ai.sessionConfirm', 'Confirm to authorize this operation with your signed-in session.')}
              </p>
              <Button
                onClick={handlePlainConfirm}
                disabled={loading}
                className="w-full gap-2"
                size="lg"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ShieldCheck className="h-4 w-4" />
                )}
                {loading ? copy('vishing.ai.confirming', 'Confirming...') : copy('vishing.ai.confirm', 'Confirm')}
              </Button>
            </div>
          )}

          {settingsLoaded && mode === "confirmation_code" && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                {copy('vishing.ai.enterCode', 'Enter your personalized confirmation code to authorize this operation.')}
              </p>
              <div className="flex gap-2">
                <Input
                  value={code}
                  onChange={(e) => {
                    setCode(e.target.value)
                    setError("")
                  }}
                  onKeyDown={(e) => e.key === "Enter" && handleCodeVerify()}
                  placeholder={copy('vishing.ai.codePlaceholder', 'Enter confirmation code')}
                  className="font-mono tracking-wider"
                  autoFocus
                />
                <Button onClick={handleCodeVerify} disabled={loading} className="shrink-0">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          )}

          {settingsLoaded && mode === "passkey" && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                {copy('vishing.ai.deviceVerify', 'Use your device’s biometric authentication (Touch ID, Face ID, or security key) to verify your identity.')}
              </p>
              <Button
                onClick={handlePasskeyVerify}
                disabled={loading}
                className="w-full gap-2"
                size="lg"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Fingerprint className="h-5 w-5" />
                )}
                {loading ? copy('vishing.ai.verifying', 'Verifying...') : copy('vishing.ai.deviceButton', 'Verify with Touch ID')}
              </Button>
            </div>
          )}

          {error && (
            <p className="text-sm text-destructive font-medium">{isVishing ? t('vishing.ai.verificationFailed') : error}</p>
          )}

          {/* 'confirm' has nothing to toggle to — there is no code or passkey
              configured — so point admins who want a stronger check at the
              settings page instead of a mode-swap that has no other mode. */}
          {settingsLoaded && mode === "confirm" ? (
            <Link
              href="/recipients/settings/ai-chat"
              className="text-xs text-primary hover:underline inline-block"
            >
              {copy('vishing.ai.settingsLink', 'Set a confirmation code or passkey in AI Chat Settings →')}
            </Link>
          ) : (
            settingsLoaded && (
              <button
                type="button"
                className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => setMode(mode === "confirmation_code" ? "passkey" : "confirmation_code")}
              >
                {mode === "confirmation_code"
                  ? hasPasskeys
                    ? copy('vishing.ai.usePasskey', 'Use passkey instead')
                    : copy('vishing.ai.registerPasskey', 'Register a passkey in AI Chat Settings to use biometric verification')
                  : copy('vishing.ai.useCode', 'Use confirmation code instead')}
              </button>
            )
          )}
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 flex justify-end">
          <Button variant="ghost" size="sm" onClick={onCancel}>
            {copy('vishing.library.cancel', 'Cancel')}
          </Button>
        </div>
      </div>
    </div>
  )
}
